import { useState } from "react";
import { History, Loader2, FileText, HeartHandshake, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { patientNotes, errMsg } from "@/api";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("es-ES", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export const PatientHistoryDialog = ({ patient }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await patientNotes(patient.id);
      setNotes(data.notes);
    } catch (e) {
      toast.error(errMsg(e, "No se pudo cargar el historial"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid={`history-button-${patient.id}`} className="rounded-full border-slate-300 text-brand-navy h-9">
          <History className="w-4 h-4" /> Historial
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-brand-navy">Historial de {patient.name}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-brand-teal mx-auto" /></div>
        ) : notes.length === 0 ? (
          <p className="text-slate-500 text-sm py-8 text-center" data-testid="history-empty">Aún no hay notas para este paciente.</p>
        ) : (
          <div className="space-y-3" data-testid="history-list">
            {notes.map((n) => (
              <div key={n.id} className="border border-slate-200 rounded-xl p-4">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-brand-navy">{fmtDate(n.created_at)}</p>
                    <p className="text-xs text-slate-500">{n.therapist_name}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {n.sent && <Badge className="bg-brand-teal/10 text-brand-teal border-0"><CheckCircle2 className="w-3 h-3 mr-1" /> Enviado</Badge>}
                    <Button variant="ghost" size="sm" className="text-brand-teal h-8" onClick={() => setExpanded(expanded === n.id ? null : n.id)}>
                      {expanded === n.id ? "Ocultar" : "Ver"}
                    </Button>
                  </div>
                </div>
                {expanded === n.id && (
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="flex items-center gap-1 text-xs font-semibold text-brand-navy mb-1"><FileText className="w-3.5 h-3.5" /> Registro clínico</p>
                      <pre className="whitespace-pre-wrap text-xs text-slate-700 font-sans">{n.clinical_record}</pre>
                    </div>
                    <div className="bg-teal-50 rounded-lg p-3">
                      <p className="flex items-center gap-1 text-xs font-semibold text-brand-navy mb-1"><HeartHandshake className="w-3.5 h-3.5 text-brand-teal" /> Documento del paciente</p>
                      <pre className="whitespace-pre-wrap text-xs text-slate-700 font-sans">{n.patient_document}</pre>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
